import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { Checkbox } from '@/components/ui/Checkbox';
import { 
  Kanban, 
  CreateKanbanRequest, 
  UpdateKanbanRequest,
  KanbanEtapa,
  CreateKanbanEtapaRequest,
  UpdateKanbanEtapaRequest,
  UserKanban,
  CreateUserKanbanRequest,
  UpdateUserKanbanRequest,
  User
} from '@/types';
import { kanbanService, userKanbanService } from '@/services/kanbanService';
import { userService } from '@/services/userService';
import { toast } from 'react-hot-toast';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff, 
  Users, 
  Settings,
  ChevronRight,
  ChevronDown
} from 'lucide-react';

export const KanbanConfigPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'kanbans' | 'users'>('kanbans');
  const [kanbanModalOpen, setKanbanModalOpen] = useState(false);
  const [etapaModalOpen, setEtapaModalOpen] = useState(false);
  const [userKanbanModalOpen, setUserKanbanModalOpen] = useState(false);
  const [editingKanban, setEditingKanban] = useState<Kanban | null>(null);
  const [editingEtapa, setEditingEtapa] = useState<KanbanEtapa | null>(null);
  const [editingUserKanban, setEditingUserKanban] = useState<UserKanban | null>(null);
  const [selectedKanbanId, setSelectedKanbanId] = useState<string>('');
  const [expandedKanbans, setExpandedKanbans] = useState<Set<string>>(new Set());

  const queryClient = useQueryClient();

  // Queries
  const { data: kanbans = [], isLoading: kanbansLoading } = useQuery({
    queryKey: ['kanbans'],
    queryFn: kanbanService.getAllKanbans,
  });

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: userService.getAllUsers,
  });

  const { data: userKanbans = [], isLoading: userKanbansLoading } = useQuery({
    queryKey: ['userKanbans'],
    queryFn: userKanbanService.getAllUserKanbans,
  });

  // Mutations
  const createKanbanMutation = useMutation({
    mutationFn: kanbanService.createKanban,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanbans'] });
      toast.success('Kanban criado com sucesso!');
      setKanbanModalOpen(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao criar kanban');
    },
  });

  const updateKanbanMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateKanbanRequest }) =>
      kanbanService.updateKanban(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanbans'] });
      toast.success('Kanban atualizado com sucesso!');
      setKanbanModalOpen(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao atualizar kanban');
    },
  });

  const deleteKanbanMutation = useMutation({
    mutationFn: kanbanService.deleteKanban,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanbans'] });
      toast.success('Kanban excluído com sucesso!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao excluir kanban');
    },
  });

  const toggleKanbanStatusMutation = useMutation({
    mutationFn: kanbanService.toggleKanbanStatus,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanbans'] });
      toast.success('Status do kanban alterado!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao alterar status');
    },
  });

  const createEtapaMutation = useMutation({
    mutationFn: (data: CreateKanbanEtapaRequest) => {
      console.log('🔍 createEtapaMutation.mutationFn chamado com:', data);
      return kanbanService.createKanbanEtapa(data);
    },
    onSuccess: (data) => {
      console.log('✅ createEtapaMutation.onSuccess:', data);
      queryClient.invalidateQueries({ queryKey: ['kanbans'] });
      toast.success('Etapa criada com sucesso!');
      setEtapaModalOpen(false);
    },
    onError: (error: any) => {
      console.error('❌ createEtapaMutation.onError:', error);
      toast.error(error.response?.data?.message || 'Erro ao criar etapa');
    },
  });

  const updateEtapaMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateKanbanEtapaRequest }) =>
      kanbanService.updateKanbanEtapa(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanbans'] });
      toast.success('Etapa atualizada com sucesso!');
      setEtapaModalOpen(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao atualizar etapa');
    },
  });

  const deleteEtapaMutation = useMutation({
    mutationFn: kanbanService.deleteKanbanEtapa,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanbans'] });
      toast.success('Etapa excluída com sucesso!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao excluir etapa');
    },
  });

  const createUserKanbanMutation = useMutation({
    mutationFn: userKanbanService.createUserKanban,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userKanbans'] });
      toast.success('Usuário associado ao kanban com sucesso!');
      setUserKanbanModalOpen(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao associar usuário');
    },
  });

  const updateUserKanbanMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserKanbanRequest }) =>
      userKanbanService.updateUserKanban(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userKanbans'] });
      toast.success('Associação atualizada com sucesso!');
      setUserKanbanModalOpen(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao atualizar associação');
    },
  });

  const deleteUserKanbanMutation = useMutation({
    mutationFn: userKanbanService.deleteUserKanban,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userKanbans'] });
      toast.success('Associação removida com sucesso!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao remover associação');
    },
  });

  // Handlers
  const handleCreateKanban = () => {
    setEditingKanban(null);
    setKanbanModalOpen(true);
  };

  const handleEditKanban = (kanban: Kanban) => {
    setEditingKanban(kanban);
    setKanbanModalOpen(true);
  };

  const handleCreateEtapa = (kanbanId: string) => {
    console.log('🔍 handleCreateEtapa chamado com kanbanId:', kanbanId);
    setSelectedKanbanId(kanbanId);
    setEditingEtapa(null);
    setEtapaModalOpen(true);
  };

  const handleEditEtapa = (etapa: KanbanEtapa) => {
    setEditingEtapa(etapa);
    setEtapaModalOpen(true);
  };

  const handleCreateUserKanban = () => {
    setEditingUserKanban(null);
    setUserKanbanModalOpen(true);
  };

  const handleEditUserKanban = (userKanban: UserKanban) => {
    setEditingUserKanban(userKanban);
    setUserKanbanModalOpen(true);
  };

  const toggleKanbanExpansion = (kanbanId: string) => {
    const newExpanded = new Set(expandedKanbans);
    if (newExpanded.has(kanbanId)) {
      newExpanded.delete(kanbanId);
    } else {
      newExpanded.add(kanbanId);
    }
    setExpandedKanbans(newExpanded);
  };

  return (
    <>
      <Helmet>
        <title>Configuração de Kanbans - PortalFly Zeh Motoca</title>
      </Helmet>

      <div className="container mx-auto px-4 py-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Configuração de Kanbans
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Gerencie kanbans e suas etapas, e configure o acesso dos usuários
          </p>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('kanbans')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'kanbans'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <Settings className="h-4 w-4 inline mr-2" />
                Kanbans
              </button>
              <button
                onClick={() => setActiveTab('users')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'users'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <Users className="h-4 w-4 inline mr-2" />
                Usuários
              </button>
            </nav>
          </div>
        </div>

        {/* Kanbans Tab */}
        {activeTab === 'kanbans' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Kanbans Cadastrados
              </h2>
              <Button onClick={handleCreateKanban}>
                <Plus className="h-4 w-4 mr-2" />
                Novo Kanban
              </Button>
            </div>

            {kanbansLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                <p className="mt-2 text-gray-600 dark:text-gray-400">Carregando kanbans...</p>
              </div>
            ) : (
              <div className="space-y-4">
                {kanbans && Array.isArray(kanbans) && kanbans.map((kanban) => (
                  <Card key={kanban.id} className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => toggleKanbanExpansion(kanban.id)}
                          className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                        >
                          {expandedKanbans.has(kanban.id) ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </button>
                        <div
                          className="w-4 h-4 rounded"
                          style={{ backgroundColor: kanban.cor }}
                        />
                        <div>
                          <h3 className="font-medium text-gray-900 dark:text-white">
                            {kanban.nome}
                            {kanban.isDefault && (
                              <Badge className="ml-2 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                Padrão
                              </Badge>
                            )}
                          </h3>
                          {kanban.descricao && (
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {kanban.descricao}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge
                          className={kanban.isAtivo ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'}
                        >
                          {kanban.isAtivo ? 'Ativo' : 'Inativo'}
                        </Badge>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCreateEtapa(kanban.id)}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Etapa
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditKanban(kanban)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toggleKanbanStatusMutation.mutate(kanban.id)}
                        >
                          {kanban.isAtivo ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => deleteKanbanMutation.mutate(kanban.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Etapas do Kanban */}
                    {expandedKanbans.has(kanban.id) && (
                      <div className="mt-4 pl-8">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-3">
                          Etapas ({kanban.etapas?.length || 0})
                        </h4>
                        <div className="space-y-2">
                          {kanban.etapas?.map((etapa) => (
                            <div
                              key={etapa.id}
                              className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                            >
                              <div className="flex items-center space-x-3">
                                <div
                                  className="w-3 h-3 rounded"
                                  style={{ backgroundColor: etapa.cor }}
                                />
                                <div>
                                  <span className="font-medium text-gray-900 dark:text-white">
                                    {etapa.nome}
                                  </span>
                                  <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">
                                    Ordem: {etapa.ordem}
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Badge
                                  className={etapa.isAtivo ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'}
                                >
                                  {etapa.isAtivo ? 'Ativo' : 'Inativo'}
                                </Badge>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleEditEtapa(etapa)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => deleteEtapaMutation.mutate(etapa.id)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                          {(!kanban.etapas || kanban.etapas.length === 0) && (
                            <p className="text-gray-500 dark:text-gray-400 text-sm">
                              Nenhuma etapa cadastrada
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </Card>
                ))}
                {(!kanbans || kanbans.length === 0) && (
                  <Card className="p-8 text-center">
                    <p className="text-gray-500 dark:text-gray-400">
                      Nenhum kanban cadastrado
                    </p>
                  </Card>
                )}
              </div>
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Associações Usuário-Kanban
              </h2>
              <Button onClick={handleCreateUserKanban}>
                <Plus className="h-4 w-4 mr-2" />
                Nova Associação
              </Button>
            </div>

            {userKanbansLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                <p className="mt-2 text-gray-600 dark:text-gray-400">Carregando associações...</p>
              </div>
            ) : (
              <div className="space-y-4">
                {userKanbans && Array.isArray(userKanbans) && userKanbans.map((userKanban) => (
                  <Card key={userKanban.id} className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div>
                          <h3 className="font-medium text-gray-900 dark:text-white">
                            {userKanban.user?.name}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {userKanban.user?.email} • {userKanban.user?.role}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Kanban: {userKanban.kanban?.nome}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditUserKanban(userKanban)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => deleteUserKanbanMutation.mutate(userKanban.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
                {(!userKanbans || userKanbans.length === 0) && (
                  <Card className="p-8 text-center">
                    <p className="text-gray-500 dark:text-gray-400">
                      Nenhuma associação cadastrada
                    </p>
                  </Card>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Kanban Modal */}
      <KanbanModal
        isOpen={kanbanModalOpen}
        onClose={() => setKanbanModalOpen(false)}
        kanban={editingKanban}
        onSave={(data) => {
          if (editingKanban) {
            updateKanbanMutation.mutate({ id: editingKanban.id, data: data as UpdateKanbanRequest });
          } else {
            createKanbanMutation.mutate(data as CreateKanbanRequest);
          }
        }}
        isEditing={!!editingKanban}
      />

      {/* Etapa Modal */}
      <EtapaModal
        isOpen={etapaModalOpen}
        onClose={() => setEtapaModalOpen(false)}
        etapa={editingEtapa}
        kanbanId={selectedKanbanId}
        onSave={(data) => {
          if (editingEtapa) {
            updateEtapaMutation.mutate({ id: editingEtapa.id, data: data as UpdateKanbanEtapaRequest });
          } else {
            createEtapaMutation.mutate(data as CreateKanbanEtapaRequest);
          }
        }}
        isEditing={!!editingEtapa}
      />

      {/* UserKanban Modal */}
      <UserKanbanModal
        isOpen={userKanbanModalOpen}
        onClose={() => setUserKanbanModalOpen(false)}
        userKanban={editingUserKanban}
        users={users}
        kanbans={kanbans}
        onSave={(data) => {
          if (editingUserKanban) {
            updateUserKanbanMutation.mutate({ id: editingUserKanban.id, data: data as UpdateUserKanbanRequest });
          } else {
            createUserKanbanMutation.mutate(data as CreateUserKanbanRequest);
          }
        }}
        isEditing={!!editingUserKanban}
      />
    </>
  );
};

// Componentes dos Modais
interface KanbanModalProps {
  isOpen: boolean;
  onClose: () => void;
  kanban: Kanban | null;
  onSave: (data: CreateKanbanRequest | UpdateKanbanRequest) => void;
  isEditing: boolean;
}

const KanbanModal: React.FC<KanbanModalProps> = ({
  isOpen,
  onClose,
  kanban,
  onSave,
  isEditing,
}) => {
  const [formData, setFormData] = useState<CreateKanbanRequest>({
    nome: '',
    descricao: '',
    cor: '#3b82f6',
  });

  useEffect(() => {
    if (kanban && isEditing) {
      setFormData({
        nome: kanban.nome,
        descricao: kanban.descricao || '',
        cor: kanban.cor,
      });
    } else {
      setFormData({
        nome: '',
        descricao: '',
        cor: '#3b82f6',
      });
    }
  }, [kanban, isEditing]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.nome.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }
    onSave(formData);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEditing ? 'Editar Kanban' : 'Novo Kanban'}>
      <div className="p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {isEditing ? 'Editar Kanban' : 'Novo Kanban'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Nome *
            </label>
            <Input
              value={formData.nome}
              onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
              placeholder="Nome do kanban"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Descrição
            </label>
            <Textarea
              value={formData.descricao}
              onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
              placeholder="Descrição do kanban"
              rows={3}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Cor
            </label>
            <div className="flex items-center space-x-2">
              <Input
                type="color"
                value={formData.cor}
                onChange={(e) => setFormData({ ...formData, cor: e.target.value })}
                className="w-16 h-10"
              />
              <Input
                value={formData.cor}
                onChange={(e) => setFormData({ ...formData, cor: e.target.value })}
                placeholder="#3b82f6"
                className="flex-1"
              />
            </div>
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit">
              {isEditing ? 'Atualizar' : 'Criar'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

interface EtapaModalProps {
  isOpen: boolean;
  onClose: () => void;
  etapa: KanbanEtapa | null;
  kanbanId: string;
  onSave: (data: CreateKanbanEtapaRequest | UpdateKanbanEtapaRequest) => void;
  isEditing: boolean;
}

const EtapaModal: React.FC<EtapaModalProps> = ({
  isOpen,
  onClose,
  etapa,
  kanbanId,
  onSave,
  isEditing,
}) => {
  const [formData, setFormData] = useState<CreateKanbanEtapaRequest>({
    kanbanId: kanbanId,
    nome: '',
    descricao: '',
    cor: '#3b82f6',
    ordem: 1,
  });

  useEffect(() => {
    if (etapa && isEditing) {
      setFormData({
        kanbanId: etapa.kanbanId,
        nome: etapa.nome,
        descricao: etapa.descricao || '',
        cor: etapa.cor,
        ordem: etapa.ordem,
      });
    } else {
      setFormData({
        kanbanId,
        nome: '',
        descricao: '',
        cor: '#3b82f6',
        ordem: 1,
      });
    }
  }, [etapa, isEditing, kanbanId]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('🔍 EtapaModal - handleSubmit chamado');
    console.log('📋 formData:', formData);
    console.log('🎯 kanbanId:', kanbanId);
    
    if (!formData.nome.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }
    if (formData.ordem < 1) {
      toast.error('Ordem deve ser um número positivo');
      return;
    }
    if (!kanbanId) {
      toast.error('ID do kanban não encontrado');
      return;
    }
    onSave(formData);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEditing ? 'Editar Etapa' : 'Nova Etapa'}>
      <div className="p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {isEditing ? 'Editar Etapa' : 'Nova Etapa'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Nome *
            </label>
            <Input
              value={formData.nome}
              onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
              placeholder="Nome da etapa"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Descrição
            </label>
            <Textarea
              value={formData.descricao}
              onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
              placeholder="Descrição da etapa"
              rows={3}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Cor
              </label>
              <div className="flex items-center space-x-2">
                <Input
                  type="color"
                  value={formData.cor}
                  onChange={(e) => setFormData({ ...formData, cor: e.target.value })}
                  className="w-16 h-10"
                />
                <Input
                  value={formData.cor}
                  onChange={(e) => setFormData({ ...formData, cor: e.target.value })}
                  placeholder="#3b82f6"
                  className="flex-1"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Ordem *
              </label>
              <Input
                type="number"
                min="1"
                value={formData.ordem}
                onChange={(e) => setFormData({ ...formData, ordem: parseInt(e.target.value) || 1 })}
                required
              />
            </div>
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit">
              {isEditing ? 'Atualizar' : 'Criar'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

interface UserKanbanModalProps {
  isOpen: boolean;
  onClose: () => void;
  userKanban: UserKanban | null;
  users: User[];
  kanbans: Kanban[];
  onSave: (data: CreateUserKanbanRequest | UpdateUserKanbanRequest) => void;
  isEditing: boolean;
}

const UserKanbanModal: React.FC<UserKanbanModalProps> = ({
  isOpen,
  onClose,
  userKanban,
  users,
  kanbans,
  onSave,
  isEditing,
}) => {
  const [formData, setFormData] = useState<CreateUserKanbanRequest>({
    userId: '',
    kanbanId: '',
    etapasIds: [],
  });

  const [selectedEtapas, setSelectedEtapas] = useState<string[]>([]);

  useEffect(() => {
    if (userKanban && isEditing) {
      setFormData({
        userId: userKanban.userId,
        kanbanId: userKanban.kanbanId,
        etapasIds: userKanban.etapasIds ? JSON.parse(userKanban.etapasIds) : [],
      });
      setSelectedEtapas(userKanban.etapasIds ? JSON.parse(userKanban.etapasIds) : []);
    } else {
      setFormData({
        userId: '',
        kanbanId: '',
        etapasIds: [],
      });
      setSelectedEtapas([]);
    }
  }, [userKanban, isEditing]);

  const selectedKanban = kanbans.find(k => k.id === formData.kanbanId);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.userId) {
      toast.error('Usuário é obrigatório');
      return;
    }
    if (!formData.kanbanId) {
      toast.error('Kanban é obrigatório');
      return;
    }
    onSave({ ...formData, etapasIds: selectedEtapas });
  };

  const handleEtapaToggle = (etapaId: string) => {
    setSelectedEtapas(prev => 
      prev.includes(etapaId) 
        ? prev.filter(id => id !== etapaId)
        : [...prev, etapaId]
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEditing ? 'Editar Associação' : 'Nova Associação'}>
      <div className="p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {isEditing ? 'Editar Associação' : 'Nova Associação'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Usuário *
            </label>
            <Select
              value={formData.userId}
              onValueChange={(value) => setFormData({ ...formData, userId: value })}
              disabled={isEditing}
            >
              <option value="">Selecione um usuário</option>
              {users && Array.isArray(users) && users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name} ({user.email}) - {user.role}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Kanban *
            </label>
            <Select
              value={formData.kanbanId}
              onValueChange={(value) => setFormData({ ...formData, kanbanId: value })}
              disabled={isEditing}
            >
              <option value="">Selecione um kanban</option>
              {kanbans && Array.isArray(kanbans) && kanbans.map((kanban) => (
                <option key={kanban.id} value={kanban.id}>
                  {kanban.nome}
                </option>
              ))}
            </Select>
          </div>
          {selectedKanban && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Etapas Permitidas
              </label>
              <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                {selectedKanban.etapas?.map((etapa) => (
                  <div key={etapa.id} className="flex items-center space-x-2">
                    <Checkbox
                      checked={selectedEtapas.includes(etapa.id)}
                      onChange={() => handleEtapaToggle(etapa.id)}
                    />
                    <div
                      className="w-3 h-3 rounded"
                      style={{ backgroundColor: etapa.cor }}
                    />
                    <span className="text-sm text-gray-900 dark:text-white">
                      {etapa.nome}
                    </span>
                  </div>
                ))}
                {(!selectedKanban.etapas || selectedKanban.etapas.length === 0) && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Nenhuma etapa disponível
                  </p>
                )}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Deixe vazio para permitir acesso a todas as etapas
              </p>
            </div>
          )}
          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit">
              {isEditing ? 'Atualizar' : 'Criar'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
